import { FC, useRef } from "react";
import { Identity, useAuthContext } from "../context/authContext";
import useOnClickOutside from "../hooks/useOnClickOutside";
import useToggle from "../hooks/useToggle";
import useI18n from "../hooks/useTranslations";
import styles from "./AccountButton.module.css";
import SvgCookie from "./icons/Cookie";
import SvgFactCheck from "./icons/FactCheck";
import SvgLogin from "./icons/Login";
import SvgLogout from "./icons/Logout";
import SvgPerson from "./icons/Person";
import SvgShieldLock from "./icons/ShieldLock";
import Icon from "./ui/Icon";
import { Divider, Menu, MenuHeader, MenuItem } from "./ui/Menu";
import Profile from "./ui/Profile";

const isMsGraphJobTitleActive = (identity: Identity) => identity.job === "ms-graph-job-title";

const AccountButton: FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const menu = useToggle();
  const { identity, init } = useAuthContext();
  const i18n = useI18n();

  useOnClickOutside(ref, menu.close);

  if (!identity) {
    return (
      <button className={styles.login} onClick={() => init(true)}>
        <Icon glyph={SvgLogin} className={styles.loginIcon} />
        {i18n.translation.common.login}
      </button>
    );
  }

  return (
    <>
      <div className={styles.personalInformation}>
        {identity && identity.firstName && (
          <div className={styles.fullName}>{identity.firstName + " " + identity.lastName}</div>
        )}
        {identity && identity.job && (
          <div className={styles.jobTitle}>
            {isMsGraphJobTitleActive(identity) ? identity.msGraphJobTitle : identity.job}
          </div>
        )}
      </div>
      <div ref={ref} className={styles.wrapper}>
        <button className={styles.button} onClick={menu.toggle}>
          <Profile layout="compact" />
        </button>
        {menu.isOpen && (
          <Menu accentColor="var(--red)" className={styles.menuPosition}>
            <MenuHeader>
              <Profile layout="full" />
            </MenuHeader>
            <Divider />
            <MenuItem
              icon={<Icon glyph={SvgPerson} />}
              label={i18n.translation.common.myProfile}
              href={import.meta.env.VITE_IAM_URL + "/me"}
              target="_blank"
              onClick={menu.close}
            />
            <MenuItem
              icon={<Icon glyph={SvgCookie} />}
              label={i18n.translation.common.cookiePolicy}
              href={import.meta.env.VITE_IAM_URL + "/cookies"}
              target="_blank"
              onClick={menu.close}
            />
            <MenuItem
              icon={<Icon glyph={SvgShieldLock} />}
              label={i18n.translation.common.privacyPolicy}
              href={import.meta.env.VITE_IAM_URL + "/privacy"}
              target="_blank"
              onClick={menu.close}
            />
            <MenuItem
              icon={<Icon glyph={SvgFactCheck} />}
              label={i18n.translation.common.termsAndConditions}
              href={import.meta.env.VITE_IAM_URL + "/terms"}
              target="_blank"
              onClick={menu.close}
            />
            <Divider />
            <MenuItem
              icon={<Icon glyph={SvgLogout} />}
              label={i18n.translation.common.logout}
              href={import.meta.env.VITE_IAM_URL + `/logout?redirect_uri=${encodeURIComponent(window.location.origin)}`}
              variant="danger"
              onClick={menu.close}
            />
          </Menu>
        )}
      </div>
    </>
  );
};

export default AccountButton;
